import React, { useEffect, useState } from 'react';
import {
  Grid,
  Box,
  Typography,
  makeStyles,
  createStyles,
  Theme,
} from '@material-ui/core';
import {
  OutageMarkerView,
  useOutageLeftPanelState,
} from '../../providers/OutageLeftPanelStateProvider';
import { useTranslation } from '../../../../hooks/useTranslation';
import PowerIcon from '../icons/PowerIcon';
import OutageHistoryIcon from '../icons/OutageHistoryIcon';
import ReportOutageIcon from '../icons/ReportOutageIcon';
import AlertIcon from '../icons/AlertIcon';
import colors from '../../../../themes/main-colors';
import clsx from 'clsx';
import { ReportOutage } from '../report-outage-left-panel/ReportOutage';
import { ReportOutageOtherLocation } from '../report-outage-other-location/ReportOutageOtherLocation';
import { OutageLookUpType } from '../../../../__generated__/pge-types';
import { OutageNotifications } from '../outage-notifications/OutageNotifications';
import { OutageHistory } from '../outage-history';
import useAuth from '../../../../hooks/useAuth';
import { ReportOutageSignIn } from '../report-outage-sign-in/ReportOutageSignIn';
import { CustomDialog } from '../common/custom-dialog';
import { MyPowerIsOn } from '../my-power-is-on';
import { useOutageAccountListContext } from '../../providers/OutageAccountListProvider';
import { DefaultViewParamState } from '../../types';
import { clearAllQueryParams } from '../../utils/utils';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    actionItemExpanded: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: theme.spacing(2.5, 0),
      cursor: 'pointer',
      textAlign: 'center',
      backgroundColor: colors.white,
    },
    actionItemCollapsed: {
      cursor: 'pointer',
      display: 'flex',
      flexDirection: 'column',
      padding: theme.spacing(1.25, 0),
      textAlign: 'center',
    },
    labelExpanded: {
      fontSize: 16,
      fontWeight: 600,
    },
    labelCollapsed: {
      fontSize: 14,
      fontWeight: 600,
    },
    borderRight: {
      borderRight: `1px solid ${colors.grey2}`,
    },
    borderTop: {
      borderTop: `1px solid ${colors.grey2}`,
    },
    borderBottom: {
      borderBottom: `1px solid ${colors.grey2}`,
    },
  }),
);

interface ActionItemProps {
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  testId?: string;
}

export const ActionItem: React.FC<ActionItemProps> = ({
  icon,
  label,
  onClick,
  disabled,
  testId,
}) => {
  const { panelOpen: expanded } = useOutageLeftPanelState();
  const { t } = useTranslation();
  const classes = useStyles();

  return (
    <Box
      data-testid={testId}
      className={
        expanded ? classes.actionItemExpanded : classes.actionItemCollapsed
      }
      onClick={disabled ? undefined : onClick}
      aria-disabled={disabled}
      style={disabled ? { pointerEvents: 'none', opacity: 0.5 } : undefined}
    >
      <Box>{icon}</Box>
      <Typography
        className={expanded ? classes.labelExpanded : classes.labelCollapsed}
      >
        {t(label)}
      </Typography>
    </Box>
  );
};

interface OutageActionsProps {
  hasOutage?: boolean;
}

export const OutageActions: React.FC<OutageActionsProps> = ({ hasOutage }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const { selectedAccountForOutage } = useOutageLeftPanelState();
  const isAccountSelected = !!selectedAccountForOutage;
  const [openNotificationOptions, setOpenNotificationOptions] = useState(false);
  const [openOutageHistory, setOpenOutageHistory] = useState<boolean>(false);
  const [openMyPowerIsOn, setOpenMyPowerIsOn] = useState<boolean>(false);
  const [openSignIn, setOpenSignIn] = useState<boolean>(false);
  const {
    panelOpen: expanded,
    setReportOutageOpen,
    accountNotFoundInfo,
    setAccountNotFoundInfo,
    outageMarkerView,
  } = useOutageLeftPanelState();
  const { accountListWithOutageInfo } = useOutageAccountListContext();
  const outageFound =
    outageMarkerView !== OutageMarkerView.NoOutage && hasOutage;

  const urlParams: any = new URLSearchParams(window.location.search);
  const defaultViewParam = urlParams.get('view');
  const defaultAccountParam = urlParams.get('account');
  const outageFoundThroughLookup =
    isAuthenticated &&
    (outageMarkerView === OutageMarkerView.NoOutage ||
      outageMarkerView === OutageMarkerView.SearchedLocation);

  useEffect(() => {
    if (
      accountListWithOutageInfo &&
      accountListWithOutageInfo?.length > 0 &&
      defaultAccountParam
    ) {
      //clear the query param from the URL.
      clearAllQueryParams();
    }

    if (
      defaultViewParam &&
      accountListWithOutageInfo &&
      accountListWithOutageInfo?.length > 0
    ) {
      const selectedAccount = accountListWithOutageInfo.find(
        acc => acc.accountNumber === defaultAccountParam,
      );

      if (!selectedAccount) {
        return;
      }

      if (defaultViewParam === DefaultViewParamState.NOTIFICATION) {
        setOpenNotificationOptions(true);
      }

      if (defaultViewParam === DefaultViewParamState.REPORT_OUTAGE) {
        setReportOutageOpen(true);
      }

      if (defaultViewParam === DefaultViewParamState.MY_POWER_IS_ON) {
        setOpenMyPowerIsOn(true);
      }
    }
  }, [accountListWithOutageInfo, defaultViewParam, defaultAccountParam]);

  return (
    <>
      <Grid
        container
        direction={expanded ? 'row' : 'column'}
        spacing={0}
        className={clsx({ [classes.borderBottom]: expanded })}
      >
        <Grid item xs className={clsx({ [classes.borderRight]: expanded })}>
          <ActionItem
            icon={
              <ReportOutageIcon
                size={expanded ? 26 : 20}
                color={hasOutage ? colors.pacificBlue : colors.orange}
              />
            }
            label={expanded ? 'REPORT_OUTAGE' : 'REPORT'}
            testId="outage-action-report"
            onClick={() => {
              if (
                !isAuthenticated &&
                (!outageMarkerView ||
                  outageMarkerView === OutageMarkerView.CRC ||
                  outageMarkerView === OutageMarkerView.CustomerAffected ||
                  outageMarkerView === OutageMarkerView.Planned ||
                  outageMarkerView === OutageMarkerView.PSPS)
              ) {
                setReportOutageOpen(false);
                const accountNotFound = accountNotFoundInfo;
                setAccountNotFoundInfo({
                  ...(accountNotFound ?? {
                    lookupType: OutageLookUpType.Account,
                    lookupValue: undefined,
                  }),
                  accountNotFoundModalOpen: true,
                });
                return;
              } else {
                setReportOutageOpen(true);
              }
            }}
            disabled={
              outageMarkerView === OutageMarkerView.SearchedLocation ||
              (isAuthenticated &&
                !isAccountSelected &&
                outageMarkerView !== OutageMarkerView.NoOutage)
            }
          />
        </Grid>
        {(!outageFound || outageFoundThroughLookup) && (
          <Grid item xs>
            <ActionItem
              icon={
                <AlertIcon
                  size={expanded ? 26 : 20}
                  color={hasOutage ? colors.pacificBlue : colors.orange}
                />
              }
              label={expanded ? 'NOTIFICATIONS' : 'NOTIFY'}
              onClick={() => {
                if (!isAuthenticated) {
                  setOpenSignIn(true);
                } else {
                  setOpenNotificationOptions(true);
                }
              }}
              disabled={
                (isAuthenticated && !isAccountSelected) ||
                (isAuthenticated &&
                  (outageMarkerView === OutageMarkerView.NoOutage ||
                    outageMarkerView === OutageMarkerView.SearchedLocation))
              }
            />
          </Grid>
        )}
      </Grid>

      {outageFound && !outageFoundThroughLookup && (
        <>
          <Grid
            container
            direction={expanded ? 'row' : 'column'}
            spacing={0}
            className={clsx(
              clsx({
                [(classes.borderTop, classes.borderBottom)]: expanded,
              }),
            )}
          >
            {/* TODO: Will bring this feature back post-go-live */}
            {/* <Grid item xs className={clsx({ [classes.borderRight]: expanded })}>
              <ActionItem
                icon={<OutageHistoryIcon size={expanded ? 26 : 20} color={hasOutage ? colors.pacificBlue : colors.orange}/>}
                label={expanded ? 'MY_OUTAGE_HISTORY' : 'HISTORY'}
                color={hasOutage ? colors.pacificBlue : colors.orange}
                onClick={() => setOpenOutageHistory(true)}
              />
            </Grid> */}
            <Grid item xs className={clsx({ [classes.borderRight]: expanded })}>
              <ActionItem
                icon={
                  <PowerIcon
                    size={expanded ? 26 : 20}
                    color={hasOutage ? colors.pacificBlue : colors.orange}
                  />
                }
                label={expanded ? 'MY_POWER_IS_ON' : 'POWER_ON'}
                onClick={() => setOpenMyPowerIsOn(true)}
              />
            </Grid>
            <Grid item xs>
              <ActionItem
                icon={
                  <AlertIcon
                    size={expanded ? 26 : 20}
                    color={hasOutage ? colors.pacificBlue : colors.orange}
                  />
                }
                label={expanded ? 'NOTIFICATIONS' : 'NOTIFY'}
                onClick={() => {
                  if (!isAuthenticated) {
                    setOpenSignIn(true);
                  } else {
                    setOpenNotificationOptions(true);
                  }
                }}
                disabled={
                  (isAuthenticated && !isAccountSelected) ||
                  (isAuthenticated &&
                    // @ts-ignore
                    (outageMarkerView === OutageMarkerView.NoOutage ||
                      outageMarkerView === OutageMarkerView.SearchedLocation))
                }
              />
            </Grid>
          </Grid>
        </>
      )}

      <ReportOutage />
      {accountNotFoundInfo?.accountNotFoundModalOpen && (
        <ReportOutageOtherLocation />
      )}
      {openNotificationOptions && (
        <OutageNotifications
          open={openNotificationOptions}
          setOpen={setOpenNotificationOptions}
        />
      )}
      {openOutageHistory && (
        <OutageHistory
          open={openOutageHistory}
          setOpen={setOpenOutageHistory}
        />
      )}
      {openMyPowerIsOn && (
        <MyPowerIsOn
          open={openMyPowerIsOn}
          setOpen={setOpenMyPowerIsOn}
          selectedAccountForOutage={selectedAccountForOutage}
        />
      )}
      {openSignIn && (
        <CustomDialog
          open={openSignIn}
          onClose={() => setOpenSignIn(false)}
          title={t('SIGN_IN')}
          dialogWidth={'600px'}
        >
          <ReportOutageSignIn />
        </CustomDialog>
      )}
    </>
  );
};
